import { getDataWithRetry, mapPagedResult } from '../fetchUtils';
import { isGym } from '../../utils/typeUtils';
import {
  isValidGymProductsPayload,
  isValidGymsResponse,
  isValidGymResponse,
} from '../apiValidators';
import { apiBaseUrl } from '../apiConsts';
import { MAX_PAGE_SIZE } from '../pagination';

import type { Gym, GymProduct, PagedResponse } from '../../types';
import type { FetchResult, FetchSuccess } from '../fetchUtils';
import { getDefaultError } from '../../utils';

const createSuccessResult = <T>(data: T): FetchSuccess<T> => ({
  success: true,
  data,
  status: 200,
  durationMs: 0,
});

export const fetchGym = async (
  gymId: string,
): Promise<FetchResult<Gym | null>> => {
  const response = await getDataWithRetry<Gym | null>(
    `${apiBaseUrl}gyms/${gymId}`,
    {},
    isGym,
  );
  return response;
};

export const fetchGymsByIds = async (
  gymIds: string[],
): Promise<FetchResult<Gym[]>> => {
  if (!gymIds || gymIds.length === 0) {
    return createSuccessResult<Gym[]>([]);
  }
  const response = await getDataWithRetry<PagedResponse<Gym>>(
    `${apiBaseUrl}gyms/getByIds?page=0&size=${MAX_PAGE_SIZE}&sort=name`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify(gymIds),
    },
    isValidGymsResponse,
  );
  return mapPagedResult(response);
};

export const fetchAllGyms = async (): Promise<FetchResult<Gym[]>> => {
  const response = await getDataWithRetry<PagedResponse<Gym>>(
    `${apiBaseUrl}gyms?page=0&size=${MAX_PAGE_SIZE}&sort=name`,
    {},
    isValidGymsResponse,
  );
  return mapPagedResult(response);
};

export const updateGymData = async (
  gymData: Gym,
): Promise<FetchResult<Gym | null>> => {
  if (!gymData.id) {
    return {
      data: null,
      success: false,
      error: getDefaultError('No gym id found'),
    };
  }
  const response = await getDataWithRetry<Gym | null>(
    `${apiBaseUrl}gyms`,
    {
      headers: {
        'content-type': 'application/json',
      },
      method: 'PUT',
      body: JSON.stringify(gymData),
    },
    isValidGymResponse,
  );
  return response;
};

export const getGymProducts = async (
  gymId: string,
): Promise<FetchResult<GymProduct[]>> => {
  const response = await getDataWithRetry<GymProduct[] | PagedResponse<GymProduct>>(
    `${apiBaseUrl}products/gym/${gymId}`,
    {},
    isValidGymProductsPayload,
  );
  if (!response.success) {
    return response;
  }

  const normalizedProducts = Array.isArray(response.data)
    ? response.data
    : response.data.content ?? [];

  return {
    ...response,
    data: normalizedProducts,
  };
};
